<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

export default {
  components: { NextButton, Icon },
  props: {
    inbox: { type: Object, required: true },
  },
  setup(props) {
    const statusData = ref(null);
    const loading = ref(true);
    const reconnecting = ref(false);
    const refreshKey = ref(0);

    const status = computed(() => statusData.value?.status || '');
    const phoneNumber = computed(() => statusData.value?.phone_number || '');
    const hasQr = computed(() => statusData.value?.has_qr === true);
    const errorMessage = computed(() => statusData.value?.error_message || '');

    const isActive = computed(() => status.value === 'active');
    const showReconnect = computed(() => !loading.value && !isActive.value);
    const showQr = computed(
      () => !isActive.value && (hasQr.value || status.value === 'pending_qr')
    );
    const showError = computed(
      () => status.value === 'error' && errorMessage.value
    );

    const qrPageUrl = computed(() => props.inbox?.qr_url || '');
    const qrImageUrl = computed(() => {
      const url = props.inbox?.qr_image_url || '';
      if (!url) return '';
      return refreshKey.value ? `${url}${url.includes('?') ? '&' : '?'}_ts=${Date.now()}` : url;
    });

    const fetchStatus = async () => {
      if (!props.inbox?.id) return;
      loading.value = true;
      try {
        const { data } = await InboxesAPI.getMaxStatus(props.inbox.id);
        statusData.value = data;
      } catch {
        statusData.value = {
          status: 'error',
          error_message: 'Failed to load status',
        };
      } finally {
        loading.value = false;
      }
    };

    // Poll the connector status while the settings page is open so an admin
    // sees the channel flip to disconnected (and the QR appear) without
    // having to refresh the page manually after a session is killed by MAX.
    const POLL_INTERVAL_MS = 30000;
    let pollHandle = null;

    onMounted(() => {
      fetchStatus();
      pollHandle = window.setInterval(fetchStatus, POLL_INTERVAL_MS);
    });

    onBeforeUnmount(() => {
      if (pollHandle !== null) {
        window.clearInterval(pollHandle);
        pollHandle = null;
      }
    });

    const refreshQrUrl = () => {
      refreshKey.value += 1;
    };

    // Re-register the channel with the connector, then show a fresh QR. Needed
    // when the connector lost this channel (connector_http_404) or when the
    // client just wants to re-pair from scratch.
    const reconnect = async () => {
      if (!props.inbox?.id || reconnecting.value) return;
      reconnecting.value = true;
      try {
        const { data } = await InboxesAPI.reconnectMax(props.inbox.id);
        statusData.value = data;
        refreshKey.value += 1;
      } catch {
        // leave the last known status in place; the poll will catch up.
      } finally {
        reconnecting.value = false;
      }
    };

    return {
      statusData,
      loading,
      reconnecting,
      status,
      phoneNumber,
      hasQr,
      errorMessage,
      isActive,
      showQr,
      showError,
      showReconnect,
      qrImageUrl,
      qrPageUrl,
      refreshKey,
      fetchStatus,
      refreshQrUrl,
      reconnect,
    };
  },
};
</script>

<template>
  <div class="max-connection-card mx-8 mt-5 overflow-hidden rounded-xl border border-n-weak bg-n-surface-2 shadow-sm">
    <div class="border-b border-n-weak bg-n-surface-1 px-4 py-3">
      <h3 class="flex items-center gap-2 text-sm font-semibold text-n-slate-12">
        <Icon icon="i-ri-chat-smile-3-line" class="text-base text-n-slate-11" />
        {{ $t('INBOX_MGMT.MAX_CONNECTION_STATUS.TITLE') }}
      </h3>
    </div>
    <div class="p-4">
      <div v-if="loading" class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-n-slate-8 border-t-n-slate-11" />
        {{ $t('INBOX_MGMT.MAX_CONNECTION_STATUS.LOADING') }}
      </div>
      <template v-else>
        <div
          v-if="isActive"
          class="flex flex-col gap-1 rounded-lg bg-n-green-2/50 px-4 py-3 ring-1 ring-n-green-6/30"
        >
          <div class="flex items-center gap-2">
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-n-green-9/20">
              <Icon icon="i-lucide-check" class="h-4 w-4 text-n-green-11" />
            </span>
            <span class="text-sm font-medium text-n-green-11">
              {{ $t('INBOX_MGMT.MAX_CONNECTION_STATUS.CONNECTED') }}
            </span>
          </div>
          <p v-if="phoneNumber" class="pl-10 text-sm text-n-slate-11">
            {{ phoneNumber }}
          </p>
        </div>
        <div
          v-else-if="showError"
          class="flex gap-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2/50 px-4 py-3"
        >
          <Icon icon="i-lucide-alert-circle" class="mt-0.5 h-5 w-5 shrink-0 text-n-ruby-10" />
          <div class="min-w-0 text-sm text-n-ruby-11">
            <span class="font-medium">{{ $t('INBOX_MGMT.MAX_CONNECTION_STATUS.ERROR_LABEL') }}:</span>
            {{ errorMessage }}
          </div>
        </div>
        <div v-if="showReconnect" class="mt-4 space-y-3 border-t border-n-weak pt-4">
          <p v-if="showQr || showError" class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.MAX_CONNECTION_STATUS.QR_HINT') }}
          </p>
          <a
            v-if="(showQr || showError) && qrPageUrl"
            :href="qrPageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-4 py-2.5 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-slate-3"
          >
            <Icon icon="i-ri-qr-code-2-line" class="h-4 w-4 text-n-slate-11" />
            {{ $t('INBOX_MGMT.MAX_CONNECTION_STATUS.OPEN_QR') }}
          </a>
          <div class="flex flex-wrap gap-2">
            <NextButton
              sm
              blue
              :is-loading="reconnecting"
              :label="$t('INBOX_MGMT.MAX_CONNECTION_STATUS.RECONNECT')"
              @click="reconnect"
            />
            <NextButton
              sm
              slate
              :label="$t('INBOX_MGMT.MAX_CONNECTION_STATUS.REFRESH')"
              @click="fetchStatus"
            />
            <NextButton
              v-if="showQr && qrImageUrl"
              sm
              slate
              :label="$t('INBOX_MGMT.ADD.MAX_CHANNEL.REFRESH_QR_BUTTON')"
              @click="refreshQrUrl"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
